
import { PageHeader } from "@/components/PageHeader";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { ClipboardList, Database, LineChart, Brain, Users, BookOpen } from "lucide-react";
import { useIsMobile } from "@/hooks/use-mobile";

const Methodology = () => {
  const isMobile = useIsMobile();
  
  const breadcrumbs = [
    { title: "Home", href: "/" },
    { title: "Methodology" },
  ];

  const methodologySteps = [
    {
      icon: ClipboardList,
      title: "Assessment Framework Development",
      content: "The AIMAA framework was developed through extensive research of industry best practices, academic literature, and consultation with AI practitioners. We analyzed over 200 successful AI implementations across various industries to identify key maturity indicators and success factors.",
      details: [
        "Comprehensive review of existing AI maturity models",
        "Analysis of industry-specific AI adoption patterns",
        "Integration of latest AI technology trends",
        "Validation through expert consultations"
      ]
    },
    {
      icon: Database,
      title: "Data Collection & Validation",
      content: "Our assessment process uses a structured questionnaire covering five core dimensions: Strategy & Leadership, Data & Infrastructure, Talent & Skills, Use Case Deployment, and Innovation & Ecosystem. Each dimension is evaluated through multiple validated metrics.",
      details: [
        "50+ carefully crafted assessment questions",
        "Industry-specific evaluation criteria",
        "Multi-stakeholder input gathering",
        "Data validation and quality checks"
      ]
    },
    {
      icon: LineChart,
      title: "Scoring Methodology",
      content: "Responses are weighted based on their relative importance, determined through statistical analysis of successful AI implementations. The scoring algorithm considers industry-specific factors and organizational context to provide accurate maturity assessments.",
      details: [
        "Advanced statistical modeling",
        "Industry-specific weightings",
        "Context-aware scoring system",
        "Continuous calibration process"
      ]
    },
    {
      icon: Brain,
      title: "AI Maturity Classification",
      content: "Organizations are classified into five maturity levels: Ad-Hoc Experimentation, Opportunistic Adoption, Strategic Integration, Embedded Intelligence, and AI-Driven Enterprise. Each level has distinct characteristics and improvement pathways.",
      details: [
        "Clearly defined maturity levels",
        "Detailed level characteristics",
        "Progressive advancement criteria",
        "Custom improvement roadmaps"
      ]
    },
    {
      icon: Users,
      title: "Benchmarking & Analysis",
      content: "Results are analyzed against industry benchmarks and peer organizations to provide context-rich insights. This comparative analysis helps organizations understand their relative position and identify priority areas for improvement.",
      details: [
        "Real-time industry benchmarks",
        "Peer group comparisons",
        "Trend analysis and forecasting",
        "Gap identification and prioritization"
      ]
    },
    {
      icon: BookOpen,
      title: "Continuous Improvement",
      content: "The framework undergoes regular updates based on new research findings, emerging AI trends, and feedback from participating organizations. This ensures the assessment remains relevant and valuable for organizations at all stages of AI adoption.",
      details: [
        "Regular framework updates",
        "Incorporation of new AI trends",
        "Participant feedback integration",
        "Methodology refinement process"
      ]
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-white via-gray-50 to-white">
      <PageHeader
        breadcrumbs={breadcrumbs}
        title="Research Methodology"
        description="Understanding the AIMAA framework and evaluation process"
      />
      
      <div className="w-full px-4 py-10">
        {/* Introduction Section */}
        <div className="max-w-6xl mx-auto mb-12 animate-fade-in">
          <Card className="bg-white/70 hover:shadow-lg transition-all duration-300 border border-gray-100">
            <CardContent className="pt-8 pb-6 px-8">
              <p className="text-lg text-gray-700 leading-relaxed mb-6">
                The AI Maturity Assessment and Alignment (AIMAA) methodology is built on rigorous research 
                and real-world validation. Our approach combines quantitative analysis with qualitative 
                insights to provide a comprehensive evaluation of an organization's AI capabilities.
              </p>
              <p className="text-lg text-gray-700 leading-relaxed">
                Through systematic assessment across multiple dimensions, we help organizations understand 
                their current AI maturity level and provide actionable recommendations for advancement.
              </p>
            </CardContent>
          </Card>
        </div>

        {/* Methodology Steps */}
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {methodologySteps.map((step, index) => (
              <Card 
                key={index} 
                className={`border border-gray-100 shadow-sm hover:shadow-lg transition-all duration-300 animate-fade-in bg-white/70`}
                style={{ animationDelay: `${index * 150}ms` }}
              >
                <CardHeader className="pb-2">
                  <div className="flex items-center gap-4">
                    <div className="p-3 bg-[#0f2d69]/10 rounded-lg">
                      <step.icon className="h-6 w-6 text-[#0f2d69]" />
                    </div>
                    <CardTitle className="text-xl font-semibold text-[#0f2d69]">
                      {step.title}
                    </CardTitle>
                  </div>
                </CardHeader>
                <CardContent>
                  <p className="text-gray-700 leading-relaxed mb-4">
                    {step.content}
                  </p>
                  <div className={`grid ${isMobile ? 'grid-cols-1' : 'grid-cols-2'} gap-3 mt-4`}>
                    {step.details.map((detail, detailIndex) => (
                      <div 
                        key={detailIndex}
                        className="bg-[#0f2d69]/5 p-3 rounded-lg text-sm text-gray-700 hover:bg-[#0f2d69]/10 transition-colors flex items-start"
                      >
                        <div className="w-2 h-2 rounded-full bg-[#0f2d69]/70 mt-1.5 mr-2 flex-shrink-0"></div>
                        <span>{detail}</span>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Methodology;
